<template>
  <div class="min-h-screen bg-zinc-950 p-8 text-zinc-100">
    <div class="max-w-4xl mx-auto space-y-8">
      
      <!-- Header -->
      <header class="flex justify-between items-center bg-zinc-900/50 backdrop-blur-md p-6 rounded-3xl shadow-sm border border-zinc-800/50 relative overflow-hidden">
        <div class="relative z-10 flex flex-col items-start gap-4 w-full">
          <router-link :to="{ name: 'music_analytics' }" class="inline-flex items-center gap-2 text-zinc-400 hover:text-green-400 transition-colors text-sm font-medium">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
            Retour aux statistiques
          </router-link>
          
          <div class="flex items-center gap-6" v-if="artist">
            <div class="w-24 h-24 rounded-full overflow-hidden bg-zinc-800 shadow-md ring-4 ring-green-500/20">
              <img v-if="artist.images && artist.images.length > 0" :src="artist.images[0].url" class="w-full h-full object-cover" />
              <div v-else class="w-full h-full flex items-center justify-center text-zinc-600">
                <svg class="w-10 h-10" fill="currentColor" viewBox="0 0 24 24"><path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/></svg>
              </div>
            </div>
            <div>
              <h1 class="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-600">{{ artist.name }}</h1>
              <p class="text-zinc-400 mt-1">{{ tracks.length }} titres likés</p>
            </div>
          </div>
        </div>
        
        <!-- Decorative blob -->
        <div class="absolute -right-10 -top-10 w-48 h-48 bg-green-500/10 rounded-full blur-3xl"></div>
      </header>

      <div v-if="loading" class="text-center py-20">
        <div class="w-16 h-16 border-4 border-green-500/20 border-t-green-500 rounded-full animate-spin mx-auto mb-4"></div>
      </div>

      <div v-else-if="error" class="bg-red-500/10 border border-red-500/20 p-8 rounded-3xl text-center">
        <p class="text-red-400">{{ error }}</p>
      </div>

      <div v-else class="bg-zinc-900/40 backdrop-blur-sm rounded-3xl border border-zinc-800/50 p-2">
        <div class="space-y-1">
          <div 
            v-for="track in tracks" 
            :key="track.spotify_id"
            class="flex items-center gap-4 p-4 rounded-2xl hover:bg-zinc-800/50 transition-all duration-300"
          >
            <!-- Icon -->
            <div class="w-10 h-10 rounded-full bg-green-500/10 text-green-500 flex items-center justify-center flex-shrink-0">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"></path></svg>
            </div>
            
            <!-- Info -->
            <div class="flex-1 min-w-0">
              <h3 class="font-bold text-zinc-200 truncate">{{ track.name }}</h3>
              <p class="text-sm text-zinc-500">Ajouté le {{ formatDate(track.added_at) }}</p>
            </div>
          </div>
          
          <div v-if="tracks.length === 0" class="p-8 text-center text-zinc-500">
            Aucun titre trouvé.
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import { useToast } from '../../composables/useToast'

const route = useRoute()
const { error: showError } = useToast()

const loading = ref(true)
const error = ref('')

const artist = ref<{name: string, images: any[]}|null>(null)
const tracks = ref<any[]>([])

const formatDate = (dateString: string) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('fr-FR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric'
  }).format(date)
}

const fetchArtistDetail = async () => {
  try {
    const id = route.params.id
    const token = localStorage.getItem('apiToken')
    const response = await fetch(`/api/music/analytics/artist/${id}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    })
    
    if (!response.ok) {
        if (response.status === 404) {
            throw new Error('Artiste introuvable.')
        }
        throw new Error('Erreur réseau')
    }
    
    const data = await response.json()
    artist.value = data.artist
    tracks.value = data.tracks
  } catch (e: any) {
    console.error(e)
    error.value = e.message || "Impossible de charger l'artiste."
    showError(error.value)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  fetchArtistDetail()
})
</script>
